import { Component } from "@theme/component";

const CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const inflight = new Map();

const DUTCH_MONTHS = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

function formatDutchDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return `${date.getDate()} ${DUTCH_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

function renderStars(rating) {
  const filled = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  return "★".repeat(filled) + "☆".repeat(5 - filled);
}

async function fetchReviews(id, publicCode, limit) {
  const cacheKey = `wwk-reviews-${id}-${limit}`;

  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const { data, expires } = JSON.parse(cached);
      if (expires > Date.now()) return data;
    }
  } catch {}

  if (inflight.has(cacheKey)) return inflight.get(cacheKey);

  const url = `https://dashboard.webwinkelkeur.nl/api/1.0/ratings.json?id=${encodeURIComponent(
    id,
  )}&public_code=${encodeURIComponent(publicCode)}&limit=${encodeURIComponent(limit)}`;

  const promise = fetch(url)
    .then((response) => response.json())
    .then((json) => {
      if (json?.status !== "success" || !Array.isArray(json.ratings)) return null;
      const data = { ratings: json.ratings, total: json.total };
      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ data, expires: Date.now() + CACHE_TTL_MS }),
        );
      } catch {}
      return data;
    })
    .catch(() => null)
    .finally(() => {
      inflight.delete(cacheKey);
    });

  inflight.set(cacheKey, promise);
  return promise;
}

class WebwinkelkeurReviews extends Component {
  async connectedCallback() {
    super.connectedCallback();

    const { webshopId, publicCode, limit } = this.dataset;
    if (!webshopId || !publicCode) return;

    const max = Number(limit) || 5;
    const data = await fetchReviews(webshopId, publicCode, max);
    if (!data?.ratings) return;

    const cards = this.querySelectorAll('[data-review-card]');
    const filterWithComment = this.dataset.requireComment === "true";

    const reviews = filterWithComment
      ? data.ratings.filter((r) => r.comment && r.comment.trim() !== "")
      : data.ratings;

    cards.forEach((card, index) => {
      const review = reviews[index];
      if (!review) {
        card.hidden = true;
        return;
      }
      card.hidden = false;

      const nameEl = card.querySelector('[data-review-name]');
      const starsEl = card.querySelector('[data-review-stars]');
      const commentEl = card.querySelector('[data-review-comment]');
      const dateEl = card.querySelector('[data-review-date]');

      if (nameEl) nameEl.textContent = review.name || "";
      if (starsEl) starsEl.textContent = renderStars(review.rating);
      if (commentEl) {
        const text = (review.comment || "").trim();
        commentEl.textContent = text;
        commentEl.hidden = text === "";
      }
      if (dateEl) dateEl.textContent = formatDutchDate(review.date || review.created);
    });
  }
}

if (!customElements.get("webwinkelkeur-reviews")) {
  customElements.define("webwinkelkeur-reviews", WebwinkelkeurReviews);
}
